import React, { useState, useRef, useEffect, useMemo } from 'react';
import VendorNav from '../../../Homepage/Components/VendorNav'
import 'react-phone-input-2/lib/style.css'
import { getVendorProfile, updateVendorProfile, changeVendorEmailInitiate, changeVendorEmailVerify } from '../../../API_Handler'
import { useToast } from '../../../../Common/ToastContext';
import { getCurrentUser, updatePassword, signOut } from 'aws-amplify/auth';
import { PasswordTurnInvisibleIcon, PasswordTurnVisibleIcon } from '../../../../Constants/Icons'
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../../UserContext';

const ProfileDetails = ({ vendorNavHidden }) => {
  const navigate = useNavigate();
  const showToast = useToast();
  const [vendorData, setVendorData] = useState(null);
  const [newVendorData, setNewVendorData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [updatePasswordModal, setUpdatePasswordModal] = useState(false);
  const [updateEmailModal, setUpdateEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [emailInitiateSuccess, setEmailInitiateSuccess] = useState(false);
  const [logoutCountdown, setLogoutCountdown] = useState(null);
  const [emailProcessing, setEmailProcessing] = useState(false);
  const countdownIntervalRef = useRef(null);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isOldPasswordVisible, setIsOldPasswordVisible] = useState(false);
  const [isNewPasswordVisible, setIsNewPasswordVisible] = useState(false);
  const [isConfirmNewPasswordVisible, setIsConfirmNewPasswordVisible] = useState(false);
  const [isPasswordUpdating, setIsPasswordUpdating] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    hasNumber: false,
    hasSpecialChar: false,
    hasUppercase: false,
    hasLowercase: false,
    longEnough: false,
    isPasswordMatch: false,
  });
  const { isSignedIn, setIsSignedIn } = useUser();

  // Detect if there are any changes
  const hasChanges = useMemo(() => {
    if (!vendorData || !newVendorData) return false;
    return JSON.stringify(vendorData) !== JSON.stringify(newVendorData);
  }, [vendorData, newVendorData]);

  const retrieveVendor = async () => {
    try {
      const retrieveVendorInformation = await getVendorProfile(showToast);
      setVendorData(retrieveVendorInformation);
      setNewVendorData(retrieveVendorInformation);
      setLoading(false);
    } catch (error) {
      setError(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    retrieveVendor();
  }, []);

  const handleSave = async () => {
    setEditLoading(true);
    try {
      await updateVendorProfile(newVendorData, showToast);
      setVendorData(newVendorData);
    } catch (error) {
      console.error('Failed to update profile', error);
    }
    setEditLoading(false);
  };

  const handleCancel = () => {
    setNewVendorData(vendorData);
  };

  const handleUpdatePassword = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setIsPasswordUpdating(true);
    try {
      const user = await getCurrentUser();
      await updatePassword({
        user, oldPassword, newPassword
      });
      setSuccessMessage('Password updated successfully.');
      setUpdatePasswordModal(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setPasswordValidation({
        hasNumber: false,
        hasSpecialChar: false,
        hasUppercase: false,
        hasLowercase: false,
        longEnough: false,
        isPasswordMatch: false,
      });
      setErrorMessage('');
      setIsPasswordUpdating(false);
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred while updating the password.');
      setIsPasswordUpdating(false);
    }
  };

  const handleCancelPasswordUpdate = () => {
    setUpdatePasswordModal(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordValidation({
      hasNumber: false,
      hasSpecialChar: false,
      hasUppercase: false,
      hasLowercase: false,
      longEnough: false,
      isPasswordMatch: false,
    });
    setErrorMessage('');
    setSuccessMessage('');
    setIsOldPasswordVisible(false);
    setIsNewPasswordVisible(false);
    setIsConfirmNewPasswordVisible(false);
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    validatePassword(value, confirmNewPassword);
    setErrorMessage('');
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmNewPassword(value);
    validatePassword(newPassword, value);
    setErrorMessage('');
  };

  const validatePassword = (password, confirmPassword) => {
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const longEnough = password.length >= 8;
    const isPasswordMatch = password === confirmPassword;

    setPasswordValidation({
      hasNumber,
      hasSpecialChar,
      hasUppercase,
      hasLowercase,
      longEnough,
      isPasswordMatch,
    });
  };

  // Optional: Warn user about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Cleanup countdown interval on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const initiateEmailChange = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setEmailProcessing(true);
    try {
      const emailResponse = await changeVendorEmailInitiate(newEmail);
      if (emailResponse && emailResponse.status === 200) {
        setEmailInitiateSuccess(true);
        setSuccessMessage('Verification code sent to your old email address. Please check your inbox.');
      } else {
        setErrorMessage('Failed to send verification code. Please try again.');
      }
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred while initiating email change.');
    } finally {
      setEmailProcessing(false);
    }
  };

  const verifyEmailChange = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    setEmailProcessing(true);
    try {
      const verifyResponse = await changeVendorEmailVerify(otp);
      if (verifyResponse && verifyResponse.status === 200) {
        setLogoutCountdown(30);

        countdownIntervalRef.current = setInterval(() => {
          setLogoutCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
              setUpdateEmailModal(false);
              setNewEmail('');
              setOtp('');
              setEmailInitiateSuccess(false);
              setLogoutCountdown(null);
              setIsSignedIn(false)
              signOut();
              navigate('/vendors/login');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setErrorMessage('Invalid verification code. Please try again.');
      }
    } catch (error) {
      setErrorMessage(error.message || 'An error occurred while verifying email change.');
    } finally {
      setEmailProcessing(false);
    }
  };

  const handleLogoutNow = () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setUpdateEmailModal(false);
    setNewEmail('');
    setOtp('');
    setEmailInitiateSuccess(false);
    setLogoutCountdown(null);
    setIsSignedIn(false);
    signOut();
    navigate('/vendors/login');
  };

  const cancelEmailChange = async () => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setUpdateEmailModal(false);
    setNewEmail('');
    setOtp('');
    setEmailInitiateSuccess(false);
    setErrorMessage('');
    setSuccessMessage('');
    setLogoutCountdown(null);
    setEmailProcessing(false);
  }

  return (
    <div className="w-full flex flex-col relative">
      {!vendorNavHidden && <VendorNav navbarColor="#26051C" />}

      <div className="w-full flex flex-col">
        {!vendorNavHidden && (
          <div className="w-full px-basePx xs:px-xsPx 1.5xl:px-[104px]">
            <h3 className="mt-[32px] 1.5xl:text-[30px] text-[#505050] font-allroundgothic font-[700] mb-0">
              My Vendor Profile
            </h3>
          </div>
        )}

        {(loading || editLoading) ? (
          <div className="d-flex justify-content-center align-items-center vh-100">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only text-black">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="absolute w-full flex flex-col items-center justify-center">
            <p className="mt-[100px] font-proximanova text-[18px] text-red mb-0">
              Error retrieving vendor information. Please try refreshing the page or try again later.
            </p>
          </div>
        ) : (
          <div className="mt-[16px] w-full flex flex-col items-center justify-center px-basePx xs:px-xsPx 1.5xl:px-[104px] mx-auto mb-12">
            <div className="w-full flex flex-col sm:flex-row justify-end">
              {hasChanges && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    className="w-full sm:w-fit text-white bg-[#76da24] hover:bg-[#5bc216] rounded-lg text-[16px] font-proximanova font-[600] px-10 py-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                    onClick={handleSave}
                    disabled={editLoading}
                  >
                    {editLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      'Save Changes'
                    )}
                  </button>

                  <button
                    className="w-full sm:w-fit text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg text-[16px] font-proximanova font-[600] px-10 py-2 transition-all shadow-sm"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div className="mt-[24px] w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-2 bg-[#F9F9F9] rounded-lg shadow-md p-6">
                <h3 className="text-[22px] font-allroundgothic font-[700] text-[#26051C] mb-4">
                  Company Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="md:col-span-3">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Vendor Name
                    </label>
                    <input
                      type="text"
                      className="w-full h-[44px] rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all"
                      value={newVendorData.company_name}
                      onChange={(e) => setNewVendorData({ ...newVendorData, company_name: e.target.value })}
                      placeholder="Vendor Name"
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Vendor Website
                    </label>
                    <input
                      className="w-full h-[44px] rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all"
                      value={newVendorData.website}
                      onChange={(e) => setNewVendorData({ ...newVendorData, website: e.target.value })}
                      placeholder="Website"
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>

                  <div className="md:col-span-6">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Vendor Description
                    </label>
                    <textarea
                      rows="4"
                      maxlength="1200"
                      className="w-full rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all resize-none"
                      value={newVendorData.vendor_bio}
                      onChange={(e) => setNewVendorData({ ...newVendorData, vendor_bio: e.target.value })}
                      placeholder="Description"
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Year Founded
                    </label>
                    <input
                      className="w-full h-[44px] rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all"
                      value={newVendorData.year_founded}
                      onChange={(e) => setNewVendorData({ ...newVendorData, year_founded: e.target.value })}
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Number of Employees
                    </label>
                    <select
                      value={newVendorData.number_of_employees}
                      onChange={(e) => setNewVendorData({ ...newVendorData, number_of_employees: e.target.value })}
                      className="w-full h-[44px] rounded-md font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none transition-all"
                    >
                      <option value="" disabled>Select Number of Employees</option>
                      <option value="0-50">0-50</option>
                      <option value="51-100">51-100</option>
                      <option value="100-500">100-500</option>
                      <option value="500+">500+</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Revenue Range
                    </label>
                    <select
                      value={newVendorData.revenue}
                      onChange={(e) => setNewVendorData({ ...newVendorData, revenue: e.target.value })}
                      className="w-full h-[44px] rounded-md bg-white font-proximanova text-[16px] border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none transition-all"
                    >
                      <option value="" disabled>Select Revenue Range</option>
                      <option value="0-149999">$0 - $149,999</option>
                      <option value="150000-299999">$150,000 - $299,999</option>
                      <option value="300000-449999">$300,000 - $449,999</option>
                      <option value="450000-599999">$450,000 - $599,999</option>
                      <option value="600000-749999">$600,000 - $749,999</option>
                      <option value="750000-899999">$750,000 - $899,999</option>
                      <option value="900000-1049999">$900,000 - $1,049,999</option>
                      <option value="1050000-1199999">$1,050,000 - $1,199,999</option>
                      <option value="1200000-1349999">$1,200,000 - $1,349,999</option>
                      <option value="1350000-1499999">$1,350,000 - $1,499,999</option>
                      <option value="1500000+">$1,500,000+</option>
                    </select>
                  </div>

                  <div className="md:col-span-6">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Service Specialties
                    </label>
                    <textarea
                      rows="4"
                      maxlength="300"
                      className="w-full rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all resize-none"
                      value={newVendorData.service_specialties}
                      onChange={(e) => setNewVendorData({ ...newVendorData, service_specialties: e.target.value })}
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>

                  <div className="md:col-span-6">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Service Description
                    </label>
                    <textarea
                      rows="4"
                      maxlength="300"
                      className="w-full rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all resize-none"
                      value={newVendorData.service_description}
                      onChange={(e) => setNewVendorData({ ...newVendorData, service_description: e.target.value })}
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>

                  <div className="md:col-span-6">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Goal
                    </label>
                    <textarea
                      rows="4"
                      maxlength="1200"
                      className="w-full rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all resize-none"
                      value={newVendorData.serve_plan}
                      onChange={(e) => setNewVendorData({ ...newVendorData, serve_plan: e.target.value })}
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      API Interest
                    </label>
                    <select
                      className="w-full h-[44px] rounded-md bg-white font-proximanova text-[16px] border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all"
                      value={newVendorData.has_api_interest === null ? false : newVendorData.has_api_interest}
                      onChange={(e) => setNewVendorData({ ...newVendorData, has_api_interest: e.target.value })}
                      disabled={vendorData.approval_status !== "APPROVED"}
                    >
                      <option value={true}>IS Interested in APIs</option>
                      <option value={false}>IS NOT Interested in APIs</option>
                    </select>
                  </div>

                  <div className="md:col-span-3">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Approval Status
                    </label>
                    <p className="w-full h-[44px] flex items-center rounded-md p-3 bg-[#ECECEC] font-proximanova text-[#6D6D6D] text-[16px] border-[1px] border-[#e1e4e8] mb-0">
                      {newVendorData.approval_status}
                    </p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 bg-[#F9F9F9] rounded-lg shadow-md p-6">
                <h3 className="text-[22px] font-allroundgothic font-[700] text-[#26051C] mb-4">
                  Contact Information
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="md:col-span-3">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Title
                    </label>
                    <input
                      className="w-full h-[44px] rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all"
                      value={newVendorData.title}
                      onChange={(e) => setNewVendorData({ ...newVendorData, title: e.target.value })}
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Contact Name
                    </label>
                    <input
                      className="w-full h-[44px] rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all"
                      value={newVendorData.contact_name}
                      onChange={(e) => setNewVendorData({ ...newVendorData, contact_name: e.target.value })}
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Contact Email
                    </label>
                    <div className="relative">
                      <p className="w-full h-[44px] flex items-center rounded-md p-3 pr-[120px] bg-[#ECECEC] font-proximanova text-[#6D6D6D] text-[16px] border-[1px] border-[#e1e4e8] mb-0 overflow-hidden text-ellipsis whitespace-nowrap">
                        {newVendorData.contact_email}
                      </p>
                      <button
                        onClick={() => setUpdateEmailModal(true)}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-charmOrange hover:text-charmHover font-proximanova font-[600] text-[14px] px-3 py-1 rounded-md hover:bg-[#FFF5F0] transition-all"
                      >
                        Change Email
                      </button>
                    </div>
                  </div>

                  <div className="md:col-span-3">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Contact Phone
                    </label>
                    <input
                      className="w-full h-[44px] rounded-md p-3 font-proximanova text-[16px] bg-white border-[#e1e4e8] border-[1px] focus:border-charmOrange focus:outline-none disabled:!bg-[#ECECEC] disabled:!text-[#6D6D6D] disabled:cursor-not-allowed transition-all"
                      value={newVendorData.contact_phone_number}
                      onChange={(e) => setNewVendorData({ ...newVendorData, contact_phone_number: e.target.value })}
                      disabled={vendorData.approval_status !== "APPROVED"}
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 bg-[#F9F9F9] rounded-lg shadow-md p-6">
                <h3 className="text-[22px] font-allroundgothic font-[700] text-[#26051C] mb-4">
                  Update Password
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="col-span-1">
                    <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={isOldPasswordVisible ? 'text' : 'password'}
                        className="w-full h-[48px] rounded-lg pl-4 pr-12 font-proximanova text-[16px] bg-[#F9FAFB] border-[1.5px] border-[#e1e4e8] focus:border-charmOrange focus:bg-white focus:outline-none transition-all"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        placeholder="Enter your current password"
                      />
                      <span
                        onClick={() => setIsOldPasswordVisible(!isOldPasswordVisible)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer text-[#6B7280] hover:text-charmOrange transition-colors"
                      >
                        {isOldPasswordVisible ? <PasswordTurnVisibleIcon /> : <PasswordTurnInvisibleIcon />}
                      </span>
                    </div>
                  </div>

                  <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="col-span-1">
                      <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={isNewPasswordVisible ? 'text' : 'password'}
                          className="w-full h-[48px] rounded-lg pl-4 pr-12 font-proximanova text-[16px] bg-[#F9FAFB] border-[1.5px] border-[#e1e4e8] focus:border-charmOrange focus:bg-white focus:outline-none transition-all"
                          value={newPassword}
                          onChange={handleNewPasswordChange}
                          placeholder="Enter your new password"
                        />
                        <span
                          onClick={() => setIsNewPasswordVisible(!isNewPasswordVisible)}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer text-[#6B7280] hover:text-charmOrange transition-colors"
                        >
                          {isNewPasswordVisible ? <PasswordTurnVisibleIcon /> : <PasswordTurnInvisibleIcon />}
                        </span>
                      </div>

                      <div className="mt-3 p-3 bg-[#F9FAFB] rounded-lg border border-[#e1e4e8]">
                        <p className="text-[13px] font-proximanova font-[600] text-[#6B7280] mb-2">Password Requirements:</p>
                        <div className="grid grid-cols-1 gap-1.5">
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasNumber ? 'bg-[#76da24]' : 'bg-[#E5E7EB]'}`}>
                              {passwordValidation.hasNumber && <span className="text-white text-[10px]">✓</span>}
                            </div>
                            <p className={`font-proximanova text-[13px] mb-0 ${passwordValidation.hasNumber ? 'text-[#76da24] font-[600]' : 'text-[#6B7280]'}`}>
                              At least 1 number
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasSpecialChar ? 'bg-[#76da24]' : 'bg-[#E5E7EB]'}`}>
                              {passwordValidation.hasSpecialChar && <span className="text-white text-[10px]">✓</span>}
                            </div>
                            <p className={`font-proximanova text-[13px] mb-0 ${passwordValidation.hasSpecialChar ? 'text-[#76da24] font-[600]' : 'text-[#6B7280]'}`}>
                              At least 1 special character
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasUppercase ? 'bg-[#76da24]' : 'bg-[#E5E7EB]'}`}>
                              {passwordValidation.hasUppercase && <span className="text-white text-[10px]">✓</span>}
                            </div>
                            <p className={`font-proximanova text-[13px] mb-0 ${passwordValidation.hasUppercase ? 'text-[#76da24] font-[600]' : 'text-[#6B7280]'}`}>
                              At least 1 uppercase letter
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.hasLowercase ? 'bg-[#76da24]' : 'bg-[#E5E7EB]'}`}>
                              {passwordValidation.hasLowercase && <span className="text-white text-[10px]">✓</span>}
                            </div>
                            <p className={`font-proximanova text-[13px] mb-0 ${passwordValidation.hasLowercase ? 'text-[#76da24] font-[600]' : 'text-[#6B7280]'}`}>
                              At least 1 lowercase letter
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.longEnough ? 'bg-[#76da24]' : 'bg-[#E5E7EB]'}`}>
                              {passwordValidation.longEnough && <span className="text-white text-[10px]">✓</span>}
                            </div>
                            <p className={`font-proximanova text-[13px] mb-0 ${passwordValidation.longEnough ? 'text-[#76da24] font-[600]' : 'text-[#6B7280]'}`}>
                              At least 8 characters
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-span-1">
                      <label className="w-full text-left font-proximanova font-[600] text-[16px] text-[#404040] mb-1 block">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={isConfirmNewPasswordVisible ? 'text' : 'password'}
                          className="w-full h-[48px] rounded-lg pl-4 pr-12 font-proximanova text-[16px] bg-[#F9FAFB] border-[1.5px] border-[#e1e4e8] focus:border-charmOrange focus:bg-white focus:outline-none transition-all"
                          value={confirmNewPassword}
                          onChange={handleConfirmPasswordChange}
                          placeholder="Confirm your new password"
                        />
                        <span
                          onClick={() => setIsConfirmNewPasswordVisible(!isConfirmNewPasswordVisible)}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 cursor-pointer text-[#6B7280] hover:text-charmOrange transition-colors"
                        >
                          {isConfirmNewPasswordVisible ? <PasswordTurnVisibleIcon /> : <PasswordTurnInvisibleIcon />}
                        </span>
                      </div>

                      {confirmNewPassword && (
                        <div className="mt-3 p-3 bg-[#F9FAFB] rounded-lg border border-[#e1e4e8]">
                          <div className="flex items-center gap-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordValidation.isPasswordMatch ? 'bg-[#76da24]' : 'bg-[#E5E7EB]'}`}>
                              {passwordValidation.isPasswordMatch && <span className="text-white text-[10px]">✓</span>}
                            </div>
                            <p className={`font-proximanova text-[13px] mb-0 ${passwordValidation.isPasswordMatch ? 'text-[#76da24] font-[600]' : 'text-[#6B7280]'}`}>
                              Passwords match
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="md:col-span-2 w-full gap-4">
                    {errorMessage && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-[14px] font-proximanova text-[#fd5764] mb-0 text-center">{errorMessage}</p>
                      </div>
                    )}
                    {successMessage && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-[14px] font-proximanova text-[#76da24] mb-0 text-center">{successMessage}</p>
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-2 w-full flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      className="flex-1 text-white bg-charmOrange hover:bg-charmHover rounded-lg text-[16px] font-proximanova font-[600] px-6 py-3 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                      onClick={handleUpdatePassword}
                      disabled={isPasswordUpdating || !passwordValidation.isPasswordMatch || !oldPassword || !newPassword}
                    >
                      {isPasswordUpdating ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Updating...
                        </span>
                      ) : (
                        'Update Password'
                      )}
                    </button>
                    <button
                      className="flex-1 text-[#6B7280] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-lg text-[16px] font-proximanova font-[600] px-6 py-3 transition-all"
                      onClick={handleCancelPasswordUpdate}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {updateEmailModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="w-[90%] sm:w-[25rem] bg-white p-[12px] sm:p-[24px] flex flex-col">
                  <h5 className="w-full text-left font-proximanova font-[600] text-[22px] text-[#505050]">
                    Update Email Address
                  </h5>

                  <label className="w-full text-left font-proximanova font-[400] text-[18px] text-[#404040] mb-1">
                    New email
                  </label>
                  <p className="w-full text-left font-proximanova font-[400] text-[14px] text-[#6D6D6D] mb-2">
                    Please note: Domain of emails must be the same
                  </p>
                  <div className="relative">
                    <input
                      type="email"
                      className="w-full h-[44px] rounded-none pl-[12px] pr-[32px] font-proximanova text-[18px] border-[1px] border-[#e1e4e8] focus:border-charmOrange focus:outline-none"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      disabled={emailInitiateSuccess}
                    />
                  </div>

                  {emailInitiateSuccess && (
                    <>
                      <label className="mt-[8px] w-full text-left font-proximanova font-[400] text-[18px] text-[#404040] mb-1">
                        Verification Code
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          className="w-full h-[44px] rounded-none pl-[12px] pr-[32px] font-proximanova text-[18px] border-[1px] border-[#e1e4e8] focus:border-charmOrange focus:outline-none"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          placeholder="Enter verification code"
                        />
                      </div>
                    </>
                  )}

                  <div className="mt-[16px] w-full flex flex-row justify-center gap-[16px]">
                    <button
                      className={`w-fit text-white rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4 ${
                        emailProcessing || logoutCountdown !== null
                          ? 'bg-[#BDBDBD] cursor-not-allowed'
                          : 'bg-[#76da24] hover:text-integrationRequestTextHover hover:bg-charmHover'
                      }`}
                      onClick={emailInitiateSuccess ? verifyEmailChange : initiateEmailChange}
                      disabled={emailProcessing || logoutCountdown !== null}
                    >
                      {emailProcessing
                        ? 'Processing...'
                        : emailInitiateSuccess ? 'Verify' : 'Send Code'
                      }
                    </button>
                    <button
                      className={`w-fit text-white rounded-full text-[18px] font-proximanova px-8 py-2 1.5xl:px-10 1.5xl:py-4 ${
                        logoutCountdown !== null
                          ? 'bg-[#BDBDBD] cursor-not-allowed'
                          : 'bg-[#fd5764] hover:text-integrationRequestTextHover hover:bg-charmHover'
                      }`}
                      onClick={cancelEmailChange}
                      disabled={logoutCountdown !== null}
                    >
                      Cancel
                    </button>
                  </div>
                  {errorMessage && <p className="text-[18px] font-proximanova text-[#fd5764] mt-3">{errorMessage}</p>}
                  {successMessage && <p className="text-[18px] font-proximanova text-[#76da24] mt-3">{successMessage}</p>}
                  {logoutCountdown !== null && (
                    <p className="text-[18px] font-proximanova text-[#76da24] mt-3">
                      Email updated successfully! Please log in again with your new email. You will automatically be logged out in {logoutCountdown} second{logoutCountdown !== 1 ? 's' : ''}.{' '}
                      <span
                        onClick={handleLogoutNow}
                        className="text-[#1e0527] underline cursor-pointer hover:text-charmOrange"
                      >
                        Log out now
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div >
  );
};

export default ProfileDetails;